// src/Components/Promo/PortfolioSettings.js

import React, { useState, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Radio from '@mui/material/Radio';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import InputAdornment from '@mui/material/InputAdornment';
import CircularProgress from '@mui/material/CircularProgress';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Checkbox from '@mui/material/Checkbox';
import Paper from '@mui/material/Paper';

// Icons
import HelpIcon from '@mui/icons-material/Help';
import CloseIcon from '@mui/icons-material/Close';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';

import InfoFilterInputs from '../../utils/InfoFilterInputs';
import ColorPicker from '../../utils/ColorPicker';

const ORANGE_COLOR = '#FF9900';
const DARK_BLUE = '#00008B';
const LIGHT_BLUE_BG = '#E3F2FD';

export default function PortfolioSettings({ selectedItem, actions, allBatches }) {
  const isDisabled = !selectedItem;
  const FIELD_WIDTH = '550px';
  const STACK_SPACING = 0.5;

  // --- STATE FOR KEYWORD MENU ---
  const [anchorEl, setAnchorEl] = useState(null);
  const [keywordList, setKeywordList] = useState([]);
  const [isLoadingKeywords, setIsLoadingKeywords] = useState(false);

  // Track cursor position to insert keywords correctly
  const [cursorPos, setCursorPos] = useState(null);
  const inputRef = useRef(null);

  // --- STATE FOR BATCH MODAL ---
  const [isBatchModalOpen, setIsBatchModalOpen] = useState(false);
  const [tempSelectedBatches, setTempSelectedBatches] = useState([]);

  const orangeBorderSx = {
    '& .MuiOutlinedInput-root': {
      '& fieldset': { borderColor: ORANGE_COLOR },
      '&:hover fieldset': { borderColor: ORANGE_COLOR, borderWidth: '2px' },
      '&.Mui-focused fieldset': { borderColor: ORANGE_COLOR, borderWidth: '2px' },
    },
  };

  const commonInputSx = {
    maxWidth: FIELD_WIDTH,
    ...orangeBorderSx,
    '& .MuiInputBase-root': {
      fontSize: '0.8rem',
      backgroundColor: LIGHT_BLUE_BG,
      color: DARK_BLUE,
      paddingRight: '4px',
    },
    '& .MuiInputBase-input': {
      padding: '6px 8px',
      color: DARK_BLUE,
      fontWeight: 'normal',
    },
    '& .MuiInputLabel-root': { top: '-5px' },
  };

  const singleLineInputSx = {
    ...commonInputSx,
    '& .MuiInputBase-root': {
      height: '30px',
      color: DARK_BLUE,
      backgroundColor: LIGHT_BLUE_BG,
      fontSize: '0.8rem',
      paddingRight: '4px',
    },
    '& .MuiInputBase-input': {
      padding: '0 8px',
      height: '100%',
      display: 'flex',
      alignItems: 'center',
      color: DARK_BLUE,
      fontWeight: 'normal',
    },
  };

  const dateInputSx = { ...singleLineInputSx, width: '150px' };

  // --- KEYWORD HANDLERS ---
  const handleInputSelect = (e) => {
    setCursorPos(e.target.selectionStart);
  };

  const handleKeywordIconClick = async (event) => {
    setAnchorEl(event.currentTarget);
    setIsLoadingKeywords(true);
    const words = await actions.fetchUniqueKeywords();
    setKeywordList(words);
    setIsLoadingKeywords(false);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleClearKeywords = () => {
    actions.handleFieldChange(selectedItem.id, 'keywords', '');
    setCursorPos(0);
    if (inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleKeywordSelect = (word) => {
    const currentText = selectedItem?.keywords || '';
    const insertAt =
      cursorPos !== null && cursorPos >= 0 && cursorPos <= currentText.length
        ? cursorPos
        : currentText.length;

    const before = currentText.substring(0, insertAt);
    const after = currentText.substring(insertAt);

    const textToInsert = `${word} `;
    const newText = `${before}${textToInsert}${after}`;

    actions.handleFieldChange(selectedItem.id, 'keywords', newText);

    const newPos = insertAt + textToInsert.length;
    setCursorPos(newPos);

    handleMenuClose();

    setTimeout(() => {
      if (inputRef.current) {
        inputRef.current.focus();
        inputRef.current.setSelectionRange(newPos, newPos);
      }
    }, 0);
  };

  // --- BATCH MODAL HANDLERS ---
  const handleOpenBatchModal = () => {
    const currentBatches = selectedItem?.Batch || [];
    setTempSelectedBatches(Array.isArray(currentBatches) ? [...currentBatches] : []);
    setIsBatchModalOpen(true);
  };

  const handleCloseBatchModal = () => {
    setIsBatchModalOpen(false);
  };

  const handleSaveBatchModal = () => {
    actions.handleFieldChange(selectedItem.id, 'Batch', tempSelectedBatches);
    setIsBatchModalOpen(false);
  };

  const handleToggleBatch = (batchId) => {
    setTempSelectedBatches((prev) => {
      if (prev.includes(batchId)) {
        return prev.filter((id) => id !== batchId);
      }
      return [...prev, batchId];
    });
  };

  const handleToggleAllBatches = (checked) => {
    if (checked) {
      const missingIds = tempSelectedBatches.filter(
        (id) => !allBatches?.some((b) => b.id === id),
      );
      const allIds = [...(allBatches || []).map((b) => b.id), ...missingIds];
      setTempSelectedBatches(allIds);
    } else {
      setTempSelectedBatches([]);
    }
  };

  const renderBatchDisplay = () => {
    if (!selectedItem) return null;
    const batches = selectedItem.Batch || [];
    if (!Array.isArray(batches) || batches.length === 0) {
      return (
        <Typography sx={{ fontSize: '0.8rem', color: '#9e9e9e' }}>
          No batches selected
        </Typography>
      );
    }

    const batchObjects = batches.map((bId) => {
      const found = allBatches?.find((b) => b.id === bId);

      let timestamp = 0;
      if (found && found.importDate) {
        const d = found.importDate;
        if (typeof d.toMillis === 'function') timestamp = d.toMillis();
        else if (typeof d.toDate === 'function') timestamp = d.toDate().getTime();
        else if (d.seconds) timestamp = d.seconds * 1000;
        else timestamp = new Date(d).getTime() || 0;
      }

      return {
        id: bId,
        filename: found ? found.filename : `${bId} (Deleted)`,
        isMissing: !found,
        timestamp,
      };
    });

    batchObjects.sort((a, b) => {
      if (a.isMissing && !b.isMissing) return 1;
      if (!a.isMissing && b.isMissing) return -1;
      return b.timestamp - a.timestamp;
    });

    return batchObjects.map((obj, idx) => {
      const color = obj.isMissing ? 'grey' : DARK_BLUE;
      return (
        <Typography key={idx} sx={{ fontSize: '0.8rem', color: color, lineHeight: 1.5 }}>
          {obj.filename}
        </Typography>
      );
    });
  };

  // Pre-calculate modal rendering array to inject missing batches
  const missingBatchIds = tempSelectedBatches.filter(
    (id) => !allBatches?.some((b) => b.id === id),
  );
  const modalBatches = [
    ...(allBatches || []),
    ...missingBatchIds.map((id) => ({
      id,
      filename: `${id} (Deleted)`,
      isMissing: true,
    })),
  ];

  return (
    <Box
      sx={{
        px: 2,
        py: 1,
        width: '100%',
        height: '100%',
        bgcolor: '#fff',
        overflowY: 'auto',
        '&::-webkit-scrollbar': { display: 'none' },
      }}>
      <Typography variant='h6' fontWeight='bold' sx={{ mt: 0, mb: 2, fontSize: '1rem' }}>
        Filter Settings
      </Typography>

      <Stack spacing={STACK_SPACING} sx={{ maxWidth: FIELD_WIDTH }}>
        <Box>
          <Typography variant='caption' sx={{ color: '#000000', fontWeight: 'bold' }}>
            Date Range
          </Typography>
          <RadioGroup
            row
            value={selectedItem?.dateRangeType || 'ALL'}
            onChange={(e) =>
              actions.handleFieldChange(selectedItem.id, 'dateRangeType', e.target.value)
            }
            sx={{ mt: -0.5 }}>
            <FormControlLabel
              value='ALL'
              control={
                <Radio
                  size='small'
                  sx={{ p: 0.5, '&.Mui-checked': { color: 'primary.main' } }}
                  disabled={isDisabled}
                />
              }
              label={
                <Typography
                  variant='body2'
                  color={isDisabled ? 'text.disabled' : 'textPrimary'}>
                  All
                </Typography>
              }
            />
            <FormControlLabel
              value='RANGE'
              control={
                <Radio
                  size='small'
                  sx={{ p: 0.5, '&.Mui-checked': { color: 'primary.main' } }}
                  disabled={isDisabled}
                />
              }
              label={
                <Typography
                  variant='body2'
                  color={isDisabled ? 'text.disabled' : 'textPrimary'}>
                  Calendar Range
                </Typography>
              }
            />
          </RadioGroup>

          {selectedItem?.dateRangeType === 'RANGE' && (
            <Box sx={{ display: 'flex', gap: 2, mt: 0.5, ml: 1 }}>
              <TextField
                label='From'
                type='date'
                size='small'
                sx={dateInputSx}
                InputLabelProps={{ shrink: true }}
                disabled={isDisabled}
                value={selectedItem?.dateFrom || ''}
                onChange={(e) =>
                  actions.handleFieldChange(selectedItem.id, 'dateFrom', e.target.value)
                }
              />
              <TextField
                label='To'
                type='date'
                size='small'
                sx={dateInputSx}
                InputLabelProps={{ shrink: true }}
                disabled={isDisabled}
                value={selectedItem?.dateTo || ''}
                onChange={(e) =>
                  actions.handleFieldChange(selectedItem.id, 'dateTo', e.target.value)
                }
              />
            </Box>
          )}
        </Box>

        {/* --- DYNAMIC BATCH TEXT FIELD AND SELECTOR --- */}
        <Box sx={{ mt: 1 }}>
          <Typography variant='caption' sx={{ color: '#000000', fontWeight: 'bold' }}>
            Batch
          </Typography>
          <Box
            sx={{
              display: 'flex',
              gap: 1,
              alignItems: 'flex-start',
              maxWidth: FIELD_WIDTH,
            }}>
            <Box
              sx={{
                flex: 1,
                minHeight: '30px',
                backgroundColor: LIGHT_BLUE_BG,
                border: `1px solid ${ORANGE_COLOR}`,
                borderRadius: '4px',
                padding: '4px 8px',
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center',
                opacity: isDisabled ? 0.6 : 1,
                pointerEvents: isDisabled ? 'none' : 'auto',
                '&:hover': {
                  borderWidth: '2px',
                  padding: '3px 7px',
                },
              }}>
              {renderBatchDisplay()}
            </Box>
            <Button
              variant='outlined'
              disabled={isDisabled}
              onClick={handleOpenBatchModal}
              sx={{
                borderColor: ORANGE_COLOR,
                color: '#000000',
                height: '30px',
                fontSize: '0.8rem',
                textTransform: 'none',
                fontWeight: 'bold',
                px: 2,
                flexShrink: 0,
                '&:hover': {
                  borderColor: ORANGE_COLOR,
                  borderWidth: '2px',
                  backgroundColor: 'rgba(255, 153, 0, 0.04)',
                },
              }}>
              Select
            </Button>
          </Box>
        </Box>

        {/* --- KEYWORDS WITH MENU ADORNMENT --- */}
        <Box>
          <Typography variant='caption' sx={{ color: '#000000', fontWeight: 'bold' }}>
            Keywords
          </Typography>
          <TextField
            fullWidth
            variant='outlined'
            size='small'
            sx={singleLineInputSx}
            placeholder='Enter keywords...'
            disabled={isDisabled}
            value={selectedItem?.keywords || ''}
            onChange={(e) =>
              actions.handleFieldChange(selectedItem.id, 'keywords', e.target.value, e)
            }
            inputRef={inputRef}
            onSelect={handleInputSelect}
            onClick={handleInputSelect}
            onKeyUp={handleInputSelect}
            InputProps={{
              endAdornment: !isDisabled && (
                <InputAdornment position='end' sx={{ gap: 0.5 }}>
                  <IconButton
                    onClick={handleKeywordIconClick}
                    size='small'
                    title='Select from existing keywords'
                    sx={{ padding: '2px' }}>
                    <HelpIcon sx={{ color: '#FF9900', fontSize: 20 }} />
                  </IconButton>

                  {selectedItem?.keywords && (
                    <IconButton
                      onClick={handleClearKeywords}
                      size='small'
                      title='Clear keywords'
                      sx={{ padding: '2px' }}>
                      <CloseIcon sx={{ color: '#9e9e9e', fontSize: 20 }} />
                    </IconButton>
                  )}
                </InputAdornment>
              ),
            }}
          />

          <Menu
            anchorEl={anchorEl}
            open={Boolean(anchorEl)}
            onClose={handleMenuClose}
            PaperProps={{
              style: { maxHeight: 300, width: '250px' },
            }}
            sx={{ zIndex: 100020 }}>
            {isLoadingKeywords ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress size={20} />
              </Box>
            ) : keywordList.length > 0 ? (
              keywordList.map((word, index) => (
                <MenuItem
                  key={index}
                  onClick={() => handleKeywordSelect(word)}
                  sx={{ fontSize: '0.875rem', py: 0.5 }}>
                  {word}
                </MenuItem>
              ))
            ) : (
              <MenuItem disabled sx={{ fontSize: '0.875rem' }}>
                No keywords found
              </MenuItem>
            )}
          </Menu>
        </Box>

        <Box sx={{ mt: 1 }}>
          <InfoFilterInputs
            filterState={selectedItem || {}}
            onFieldChange={(field, value) =>
              actions.handleFieldChange(selectedItem.id, field, value)
            }
            showLabel={true}
            disabled={isDisabled}
          />
        </Box>

        <Typography
          variant='subtitle2'
          sx={{
            color: '#000000',
            fontWeight: 'bold',
            mt: 2,
            mb: 0,
            borderBottom: '1px solid #ccc',
            pb: 0.5,
          }}>
          Other Settings
        </Typography>

        <Box sx={{ display: 'flex', gap: 2, width: '100%', mt: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant='caption' sx={{ color: '#000000', fontWeight: 'bold' }}>
              Photo dim. (mm)
            </Typography>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              type='number'
              sx={singleLineInputSx}
              placeholder='0'
              disabled={isDisabled}
              value={selectedItem?.photoDimension || ''}
              onChange={(e) =>
                actions.handleFieldChange(
                  selectedItem.id,
                  'photoDimension',
                  e.target.value,
                )
              }
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant='caption' sx={{ color: '#000000', fontWeight: 'bold' }}>
              Max Results
            </Typography>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              type='number'
              sx={singleLineInputSx}
              placeholder='0'
              disabled={isDisabled}
              value={selectedItem?.maxResults || ''}
              onChange={(e) =>
                actions.handleFieldChange(selectedItem.id, 'maxResults', e.target.value)
              }
            />
          </Box>
        </Box>

        {/* --- NEW HILIGHT COLOUR SETTINGS --- */}
        <Box sx={{ display: 'flex', gap: 2, width: '100%', mt: 1, pb: 2 }}>
          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <Typography variant='caption' sx={{ color: '#000000', fontWeight: 'bold' }}>
              Hilight Colour
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <ColorPicker
                value={selectedItem?.hilightColor || '#F5F5F5'}
                onChange={(color) =>
                  actions.handleFieldChange(selectedItem.id, 'hilightColor', color)
                }
                label='Select Hilight Colour'
                sx={{ width: 30, height: 30 }}
              />
            </Box>
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant='caption' sx={{ color: '#000000', fontWeight: 'bold' }}>
              Opacity (%)
            </Typography>
            <TextField
              fullWidth
              variant='outlined'
              size='small'
              type='number'
              inputProps={{ min: 0, max: 100, step: 5 }}
              sx={singleLineInputSx}
              placeholder='100'
              disabled={isDisabled}
              value={
                selectedItem?.hilightOpacity !== undefined
                  ? selectedItem.hilightOpacity
                  : 100
              }
              onChange={(e) =>
                actions.handleFieldChange(
                  selectedItem.id,
                  'hilightOpacity',
                  e.target.value,
                )
              }
            />
          </Box>
        </Box>
      </Stack>

      {/* --- BATCH SELECTION MODAL --- */}
      <Dialog
        open={isBatchModalOpen}
        onClose={handleCloseBatchModal}
        maxWidth='sm'
        fullWidth
        disableRestoreFocus
        sx={{ zIndex: 100020 }}>
        <Box sx={{ p: 2, backgroundColor: '#f4f4f4', borderBottom: '1px solid #ccc' }}>
          <Typography variant='subtitle1' fontWeight='bold'>
            Select Batches
          </Typography>
        </Box>
        <DialogContent sx={{ p: 0, height: '400px' }}>
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{ height: '100%', borderRadius: 0 }}>
            <Table stickyHeader size='small'>
              <TableHead>
                <TableRow
                  sx={{
                    height: '40px',
                    '& th': {
                      backgroundColor: '#fff3e0',
                      fontWeight: 'bold',
                      borderBottom: '2px solid #ff9800',
                      py: 0,
                    },
                  }}>
                  <TableCell padding='checkbox' sx={{ width: '50px' }}>
                    <Checkbox
                      size='small'
                      checked={
                        modalBatches.length > 0 &&
                        tempSelectedBatches.length === modalBatches.length
                      }
                      indeterminate={
                        tempSelectedBatches.length > 0 &&
                        tempSelectedBatches.length < modalBatches.length
                      }
                      onChange={(e) => handleToggleAllBatches(e.target.checked)}
                    />
                  </TableCell>
                  <TableCell>Filename</TableCell>
                  <TableCell sx={{ width: '120px' }}>Date</TableCell>
                  <TableCell sx={{ width: '100px' }}>Time</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {modalBatches.map((batch) => {
                  const isSelected = tempSelectedBatches.includes(batch.id);
                  const isMissing = batch.isMissing;

                  const dateObj =
                    !isMissing && batch.importDate
                      ? typeof batch.importDate.toDate === 'function'
                        ? batch.importDate.toDate()
                        : new Date(batch.importDate)
                      : null;

                  const dateStr = dateObj
                    ? dateObj.toLocaleDateString()
                    : isMissing
                      ? 'N/A'
                      : '';
                  const timeStr = dateObj
                    ? dateObj.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    : isMissing
                      ? 'N/A'
                      : '';

                  return (
                    <TableRow
                      key={batch.id}
                      hover
                      selected={isSelected}
                      onClick={() => handleToggleBatch(batch.id)}
                      sx={{ cursor: 'pointer' }}>
                      <TableCell padding='checkbox'>
                        <Checkbox
                          size='small'
                          checked={isSelected}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => handleToggleBatch(batch.id)}
                        />
                      </TableCell>
                      <TableCell sx={{ color: isMissing ? 'grey' : 'inherit' }}>
                        {batch.filename}
                      </TableCell>
                      <TableCell sx={{ color: isMissing ? 'grey' : 'inherit' }}>
                        {dateStr}
                      </TableCell>
                      <TableCell sx={{ color: isMissing ? 'grey' : 'inherit' }}>
                        {timeStr}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions
          sx={{ p: 2, backgroundColor: '#f4f4f4', borderTop: '1px solid #ccc' }}>
          <IconButton onClick={handleCloseBatchModal} sx={{ color: 'red', p: 0 }}>
            <CancelIcon sx={{ fontSize: 32 }} />
          </IconButton>
          <IconButton onClick={handleSaveBatchModal} sx={{ color: 'green', p: 0, ml: 2 }}>
            <CheckCircleIcon sx={{ fontSize: 32 }} />
          </IconButton>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
